import { nextTick, onBeforeUnmount, ref } from 'vue'
import type { InjectionKey, Ref } from 'vue'
import { computePosition, flip, offset, shift } from '@floating-ui/dom'

const SUBMENU_CLOSE_DELAY_MS = 150

// Provided by SfDropdown so SfDropdownItem trigger rows can bind
// aria-expanded reactively (the custom element instead syncs the attribute
// on its server-rendered light DOM via syncTriggerAria).
export const ACTIVE_SUBMENU: InjectionKey<Ref<string | null>> =
  Symbol('activeSubmenu')

interface Options {
  // Keep aria-expanded in sync on the trigger rows by writing the attribute
  // directly. Only for hosts whose trigger rows are not framework-rendered
  // (the custom element's light DOM); Vue-rendered rows bind it instead.
  syncTriggerAria?: boolean
}

interface DropdownSubmenu {
  activeSubmenu: Ref<string | null>
  handleMenuClick: (event: MouseEvent) => void
  handleMenuHover: (event: MouseEvent) => void
  handleMenuKeydown: (event: KeyboardEvent) => void
  isSubmenuInteraction: (event: MouseEvent) => boolean
  resetSubmenu: () => void
  scheduleSubmenuClose: () => void
}

/**
 * Shared flyout sub-menu behaviour for the dropdown family — the
 * sf-ce-dropdown-wrapper custom element and the SfDropdown Vue component.
 *
 * Contract with the rendered markup:
 * - a row carrying data-submenu-trigger="<id>" opens the submenu-<id> panel
 *   on hover or click; on touch devices (no hover) a repeated click on the
 *   same trigger closes it — the only close affordance there
 * - an element carrying data-submenu-back closes the open panel, and so
 *   does the Escape key
 * - panels carry slot="submenu-<id>" (custom-element light DOM) or
 *   data-submenu-panel="<id>" (Vue templates, where the slot attribute is
 *   deprecated) and live inside the .sf-dropdown2 container; they are
 *   positioned next to the .sf-dropdown2-menu box on the side the drawer
 *   opens towards (right by default, left for open-drawer-left menus; 4px
 *   gap, first option level with the trigger row), flipping and shifting
 *   at viewport edges
 * - hovering other menu content schedules a delayed close, so the pointer
 *   can travel across padding and the gap without dismissing the panel
 *
 * The DOM traversal below (composedPath, closest, querySelector) is
 * deliberate: the custom element's panels are server-rendered light DOM
 * that template refs cannot reach, and event delegation is the one
 * mechanism that works identically for both dropdown implementations.
 */
export function useDropdownSubmenu(options: Options = {}): DropdownSubmenu {
  const activeSubmenu = ref<string | null>(null)

  let submenuCloseTimer: ReturnType<typeof setTimeout> | null = null
  // The id a hover opened, consumed by the click of the same gesture: on
  // touch, taps emit a compatibility mouseover before the click, so without
  // this marker the click would read the tap-opened panel as a repeated
  // click and immediately toggle it closed again.
  let openedByHover: string | null = null
  let expandedTrigger: HTMLElement | null = null

  function cancelScheduledSubmenuClose(): void {
    if (submenuCloseTimer) {
      clearTimeout(submenuCloseTimer)
      submenuCloseTimer = null
    }
  }

  function scheduleSubmenuClose(): void {
    cancelScheduledSubmenuClose()
    submenuCloseTimer = setTimeout(() => {
      activeSubmenu.value = null
      syncExpandedTrigger(null)
    }, SUBMENU_CLOSE_DELAY_MS)
  }

  function resetSubmenu(): void {
    cancelScheduledSubmenuClose()
    openedByHover = null
    activeSubmenu.value = null
    syncExpandedTrigger(null)
  }

  // aria-expanded lives on the row's button when there is one (the Rails
  // list item renders the label inside a button), on the row itself
  // otherwise.
  function ariaTarget(trigger: HTMLElement): HTMLElement {
    return trigger.querySelector('button') ?? trigger
  }

  function syncExpandedTrigger(trigger: HTMLElement | null): void {
    if (!options.syncTriggerAria) return

    if (expandedTrigger && expandedTrigger !== trigger) {
      ariaTarget(expandedTrigger).setAttribute('aria-expanded', 'false')
    }
    expandedTrigger = trigger
    if (trigger) {
      ariaTarget(trigger).setAttribute('aria-expanded', 'true')
    }
  }

  function openSubmenu(trigger: HTMLElement): void {
    activeSubmenu.value = trigger.dataset.submenuTrigger ?? null
    syncExpandedTrigger(trigger)
    void positionSubmenu(trigger)
  }

  async function positionSubmenu(trigger: HTMLElement): Promise<void> {
    const id = trigger.dataset.submenuTrigger
    const panel = trigger
      .closest('.sf-dropdown2')
      ?.querySelector<HTMLElement>(
        `[slot="submenu-${id}"], [data-submenu-panel="${id}"]`
      )
    if (!panel) return

    // wait for the reactive flush so the panel is revealed and measurable
    await nextTick()

    // The panel opens on the same side the menu drawer opens towards (the
    // open-drawer-* class set from drawer_direction / drawerDirection),
    // right by default; flip() still swaps sides at the viewport edge.
    const menuBox = trigger.closest('.sf-dropdown2-menu')
    const opensLeft = menuBox?.classList.contains('open-drawer-left') ?? false

    // The trigger row is inset from the menu box by the section padding; add
    // it to the offset so the gap is measured from the menu box edge, not
    // the row edge.
    const triggerInset = menuBox
      ? opensLeft
        ? trigger.getBoundingClientRect().left -
          menuBox.getBoundingClientRect().left
        : menuBox.getBoundingClientRect().right -
          trigger.getBoundingClientRect().right
      : 0

    const { x, y } = await computePosition(trigger, panel, {
      placement: opensLeft ? 'left-start' : 'right-start',
      middleware: [
        offset({ mainAxis: 4 + triggerInset, crossAxis: -8 }),
        flip(),
        shift({ padding: 8 }),
      ],
    })

    Object.assign(panel.style, {
      left: `${x}px`,
      top: `${y}px`,
      // neutralise the menu-box positioning the panel inherits from its
      // .sf-dropdown2-menu classes (open-drawer-left's right: 0 and tw-mt-2)
      right: 'auto',
      marginTop: '0',
    })
  }

  function isSubmenuPanel(element: HTMLElement): boolean {
    return (
      element.slot?.startsWith('submenu-') ||
      element.dataset.submenuPanel !== undefined
    )
  }

  // Click opens a submenu from its trigger row; on touch devices a second
  // click on the same trigger closes it. On hover-capable devices the click
  // never closes, because moving the mouse onto the trigger has already
  // opened the submenu via hover and the click would immediately dismiss it.
  // A click belonging to the same gesture as the hover that opened the panel
  // (a tap's compatibility mouseover) never counts as a repeated click.
  function handleMenuClick(event: MouseEvent): void {
    for (const target of event.composedPath()) {
      if (!(target instanceof HTMLElement)) continue

      if (target.dataset.submenuBack !== undefined) {
        resetSubmenu()
        return
      }

      if (target.dataset.submenuTrigger) {
        cancelScheduledSubmenuClose()
        const openedInThisGesture =
          openedByHover === target.dataset.submenuTrigger
        openedByHover = null
        const isRepeatedClick =
          activeSubmenu.value === target.dataset.submenuTrigger &&
          !openedInThisGesture
        const canHover = window.matchMedia?.('(hover: hover)').matches ?? false
        if (isRepeatedClick && !canHover) {
          activeSubmenu.value = null
          syncExpandedTrigger(null)
        } else {
          openSubmenu(target)
        }
        return
      }
    }
  }

  // Hover opens/switches the submenu while the pointer is on a trigger row
  // and keeps it open while the pointer is inside the panel itself. Any
  // other menu content schedules a delayed close. Re-hovering the already
  // active trigger only refreshes the close timer, so mouseover re-runs
  // (one per row/span crossing) never re-trigger positioning.
  function handleMenuHover(event: MouseEvent): void {
    for (const target of event.composedPath()) {
      if (!(target instanceof HTMLElement)) continue

      if (target.dataset.submenuTrigger) {
        cancelScheduledSubmenuClose()
        if (activeSubmenu.value !== target.dataset.submenuTrigger) {
          openedByHover = target.dataset.submenuTrigger
          openSubmenu(target)
        }
        return
      }

      if (isSubmenuPanel(target)) {
        cancelScheduledSubmenuClose()
        return
      }
    }

    scheduleSubmenuClose()
  }

  // Escape closes the open panel and stays inside the dropdown; with no
  // panel open the event is left for the host to handle.
  function handleMenuKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Escape' || !activeSubmenu.value) return

    event.stopPropagation()
    resetSubmenu()
  }

  // True when the event happened on a submenu trigger, back element or
  // panel — interactions the host dropdown should not treat as its own
  // (e.g. SfDropdown toggles on any root click).
  function isSubmenuInteraction(event: MouseEvent): boolean {
    for (const target of event.composedPath()) {
      if (!(target instanceof HTMLElement)) continue

      if (
        target.dataset.submenuTrigger ||
        target.dataset.submenuBack !== undefined ||
        isSubmenuPanel(target)
      ) {
        return true
      }
    }

    return false
  }

  onBeforeUnmount(cancelScheduledSubmenuClose)

  return {
    activeSubmenu,
    handleMenuClick,
    handleMenuHover,
    handleMenuKeydown,
    isSubmenuInteraction,
    resetSubmenu,
    scheduleSubmenuClose,
  }
}
